/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { blockImages } from 'src/core/content-config/best-of-block-images';
import { forwardRef } from 'react';
import { ifProp } from 'styled-tools';
import { media } from 'src/styles/media';
import BlocksImagesBlocks from 'src/components/sections/episode/best-of-section/blocks-images';
import Container from 'src/components/core/layout/container';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import RawHtml from 'src/components/core/raw-html';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  data: TourData['sections']['bestOf'];
  slug: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;

  ${media.min.md`
    background-color: var(--color-grey-100);
    height: 100vh;
  `}

  ${media.max.md`
    margin-bottom: 5.75rem;
  `}
`;

/**
 * `ContainerStyle` styled component.
 */

const ContainerStyle = styled(Container)`
  ${media.min.md`
    display: flex;
    height: 100%;
    padding: 0 4.5rem;
  `}

  ${media.min.xxl`
    padding: 0 calc((100vw - var(--container-max-width)) / 2 + var(--gutter));
  `}
`;

/**
 * `FirstGrid` styled component.
 */

const FirstGrid = styled.div`
  ${media.min.md`
    display: grid;
    grid-column-gap: var(--gutter);
    grid-template-areas:
      '.            .            image1     image1     image1 .     '
      'titleWrapper titleWrapper image1     image1     image1 .     '
      'description  .            .          .          image2 image2'
      'description  .            textImage1 textImage1 image2 image2';
    grid-template-columns: 3fr 1fr 4fr 1fr 1fr 2fr;
    grid-template-rows: 1fr 2fr 5fr 2fr;
    padding: 9.8vw 0;
    width: 150vw;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw;
    width: 120vw;
  `}

  ${media.min.xl`
    grid-template-areas:
      '.            .            image1     image1 image1 .     '
      'titleWrapper titleWrapper image1     image1 image1 .     '
      'description  .            .          .      image2 image2'
      'description  .            textImage1 .      image2 image2';
  `}

  ${media.min.xxl`
    width: 94vw;
  `}
`;

/**
 * `SecondGrid` styled component.
 */

const SecondGrid = styled.div`
  ${media.min.md`
    display: grid;
    grid-column-gap: var(--gutter);
    grid-template-areas:
      'image3       textImage3  textImage3 . image5'
      'image3       .           image4     . image5'
      '.            .           image4     . image5';
    grid-template-columns: 3fr 1fr 2fr 1fr 5fr;
    grid-template-rows: 5fr 1.5fr 4fr;
    padding: 9.8vw 0 9.8vw 4.5rem;
    width: 160vw;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw 4.5rem;
    width: 115vw;
  `}

  ${media.min.xxl`
    padding-left: calc((100vw - var(--container-max-width)) / 2 + var(--gutter));
    width: 96vw;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  position: relative;
  z-index: 1;

  ${media.min.md`
    grid-area: titleWrapper;
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Text)`
  color: var(--color-yellow);
  margin-bottom: 1.5rem;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  ${media.max.md`
    margin-bottom: 1.25rem;
    text-align: center;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  margin: 0;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  ${media.max.md`
    margin-bottom: 4rem;
    text-align: center;
  `}
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  ${media.min.md`
    align-self: center;
    grid-area: description;
    margin: 2rem 0 0;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(RawHtml)`
  color: var(--color-grey-700);
  font-family: var(--font-futura-pt);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  letter-spacing: clamp(${0.4 / baseFontSize}rem, ${clampCalc(0.4, 0.5)}, ${0.5 / baseFontSize}rem);
  line-height: clamp(${25 / baseFontSize}rem, ${clampCalc(25, 30)}, ${30 / baseFontSize}rem);
  margin: 0 0 4.5rem;
`;

/**
 * `TextHandWritten` styled component.
 */

const TextHandWritten = styled(Text)`
  color: var(--color-yellow);
  display: block;
  margin: 1rem 0 0;
  text-align: center;
  text-transform: none;

  ${media.min.md`
    margin: 0.25rem 0 0;
    text-align: left;
  `}

  @supports (-webkit-hyphens: none) {
    font-size: clamp(35px, ${clampCalc(35, 40)}, 40px);

    ${media.min.md`
      margin: 0;
      position: relative;
      top: -1rem;
    `}

    ${media.max.md`
      margin-top: 0;
    `}
  }
`;

/**
 * `Image1Wrapper` styled component.
 */

const Image1Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.min.md`
    grid-area: image1;
    grid-row: 1 / span 3;
    margin-bottom: 1.25rem;
  `}

  ${media.max.md`
    margin-bottom: 0.75rem;
    padding-bottom: 122.81%;
  `}
`;

/**
 * `TextImage1Wrapper` styled component.
 */

const TextImage1Wrapper = styled.div`
  ${media.min.md`
    grid-area: textImage1;
  `}

  ${media.max.md`
    margin-bottom: 1.875rem;
  `}
`;

/**
 * `TitleImage1Wrapper` styled component.
 */

const TitleImage1Wrapper = styled.h3`
  color: var(--color-dark);
  font-family: var(--font-futura-pt);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  letter-spacing: clamp(${0.4 / baseFontSize}rem, ${clampCalc(0.4, 0.5)}, ${0.5 / baseFontSize}rem);
  line-height: clamp(${25 / baseFontSize}rem, ${clampCalc(25, 30)}, ${30 / baseFontSize}rem);
  margin-bottom: 0.5rem;
`;

/**
 * `DescriptionImage1Wrapper` styled component.
 */

const DescriptionImage1Wrapper = styled.p`
  color: var(--color-grey-500);
  font-family: var(--font-futura-pt);
  font-size: 1rem;
  letter-spacing: 0.025rem;
  line-height: clamp(${20 / baseFontSize}rem, ${clampCalc(20, 22)}, ${22 / baseFontSize}rem);
`;

/**
 * `TextImage2Wrapper` styled component.
 */

const TextImage2Wrapper = styled.div`
  ${media.min.md`
    align-items: stretch;
    display: flex;
    flex-direction: column;
    grid-area: image2;
  `}

  ${media.max.md`
    margin-bottom: 1.875rem;
  `}
`;

/**
 * `Image2Wrapper` styled component.
 */

const Image2Wrapper = styled.div`
  margin-bottom: 0.75rem;
  overflow: hidden;
  position: relative;

  ${media.min.md`
    height: 100%;
    margin-bottom: 0.5rem;
  `}

  ${media.max.md`
    padding-bottom: 122.81%;
  `}
`;

/**
 * `Image3Wrapper` styled component.
 */

const Image3Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  img {
    border: 10px solid var(--color-dark) !important;
  }

  ${media.min.md`
    grid-area: image3;
  `}

  ${media.max.md`
    margin-bottom: 0.75rem;
    padding-bottom: 122.81%;
  `}
`;

/**
 * `TextImage2Wrapper` styled component.
 */

const TextImage3Wrapper = styled.div`
  ${media.min.md`
    align-self: center;
    grid-area: textImage3;
    padding: 1.5rem 0;
  `}

  ${media.min.lg`
    margin-left: 2.5rem;
  `}

  ${media.min.xl`
    margin-left: 5.25rem;
  `}

  ${media.max.md`
    margin-bottom: 1.875rem;
  `}
`;

/**
 * `TextImage4Wrapper` styled component.
 */

const TextImage4Wrapper = styled.div`
  ${media.min.md`
    align-items: stretch;
    display: flex;
    flex-direction: column;
    grid-area: image4;
  `}

  ${media.max.md`
    margin-bottom: 1.75rem;
  `}
`;

/**
 * `FadeInAnimationTextImage4Wrapper` styled component.
 */

const FadeInAnimationTextImage4Wrapper = styled(FadeInAnimation)`
  ${media.min.md`
    align-items: stretch;
    display: flex;
    flex-direction: column;
 `}
`;

/**
 * `Image4Wrapper` styled component.
 */

const Image4Wrapper = styled.div<{ hasMargin?: boolean }>`
  overflow: hidden;
  position: relative;

  ${media.min.md`
    height: 100%;
    margin-bottom: 0.5rem;
  `}

  ${media.max.md`
    margin-bottom: 0.75rem;
    padding-bottom: 122.81%;
  `}

  ${ifProp(
    'hasMargin',
    css`
      ${media.min.md`
      margin-bottom: 27%;
    `}
    `
  )}
`;

/**
 * `Image5Wrapper` styled component.
 */

const Image5Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.min.md`
    grid-area: image5;
    height: 100%;
  `}

  ${media.max.md`
    margin-bottom: 1.875rem;
    padding-bottom: 122.81%;
  `}
`;

/**
 * `BestOfSection` component.
 */

const BestOfSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { data, slug } = props;
  const isMobile = useBreakpoint('md', 'max');

  return (
    <Wrapper ref={ref}>
      <ContainerStyle fluid>
        <FirstGrid>
          <TitleWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <SubTitle as={'p'} variant={'header'}>
                  {data.subTitle}
                </SubTitle>
              </FadeInRightAnimation>
            )}

            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {data.title}

                  <TextHandWritten as={'span'} variant={'handwrittenS'}>
                    {data.titleCity.replaceAll('-', '-⁠')}
                  </TextHandWritten>
                </Title>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <SubTitle as={'p'} variant={'header'}>
                  {data.subTitle}
                </SubTitle>
              </FadeInUpAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'boldM'}>
                  {data.title}

                  <TextHandWritten as={'span'} variant={'handwrittenS'}>
                    {data.titleCity}
                  </TextHandWritten>
                </Title>
              </FadeInUpAnimation>
            )}
          </TitleWrapper>

          <DescriptionWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Description>{data.description}</Description>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Description>{data.description}</Description>
              </FadeInUpAnimation>
            )}
          </DescriptionWrapper>

          <Image1Wrapper>
            <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
              <Image alt={data.image1.title} layout={'fill'} objectFit={'cover'} src={data.image1.image} />
            </FadeInAnimation>
          </Image1Wrapper>

          <TextImage1Wrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                {data.image1.title && <TitleImage1Wrapper>{data.image1.title}</TitleImage1Wrapper>}

                {data.image1.description && (
                  <DescriptionImage1Wrapper>{data.image1.description}</DescriptionImage1Wrapper>
                )}
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                {data.image1.title && <TitleImage1Wrapper>{data.image1.title}</TitleImage1Wrapper>}

                {data.image1.description && (
                  <DescriptionImage1Wrapper>{data.image1.description}</DescriptionImage1Wrapper>
                )}
              </FadeInUpAnimation>
            )}
          </TextImage1Wrapper>

          <TextImage2Wrapper>
            <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
              <Image2Wrapper>
                <Image alt={data.image2.title} layout={'fill'} objectFit={'cover'} src={data.image2.image} />
              </Image2Wrapper>
            </FadeInAnimation>
          </TextImage2Wrapper>
        </FirstGrid>

        <SecondGrid>
          <Image3Wrapper>
            <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
              <Image alt={data.image3.title} layout={'fill'} objectFit={'cover'} src={data.image3.image} />
            </FadeInAnimation>
          </Image3Wrapper>

          <TextImage3Wrapper>
            {!isMobile && (
              <FadeInRightAnimation fullHeight options={{ transitionTime: 1 }}>
                {data.image3.title && <TitleImage1Wrapper>{data.image3.title}</TitleImage1Wrapper>}

                {data.image3.description && (
                  <RawHtml element={DescriptionImage1Wrapper}>{data.image3.description}</RawHtml>
                )}
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation fullHeight options={{ transitionTime: 1 }}>
                {data.image3.title && <TitleImage1Wrapper>{data.image3.title}</TitleImage1Wrapper>}

                {data.image3.description && (
                  <RawHtml element={DescriptionImage1Wrapper}>{data.image3.description}</RawHtml>
                )}
              </FadeInUpAnimation>
            )}
          </TextImage3Wrapper>

          <TextImage4Wrapper>
            <FadeInAnimationTextImage4Wrapper fullHeight options={{ transitionTime: 1 }}>
              <Image4Wrapper hasMargin={isEmpty(data.image4.title)}>
                <Image alt={data.image4?.title} layout={'fill'} objectFit={'cover'} src={data.image4.image} />
              </Image4Wrapper>
            </FadeInAnimationTextImage4Wrapper>
          </TextImage4Wrapper>

          <Image5Wrapper>
            <Image alt={data.image3?.title} layout={'fill'} objectFit={'cover'} src={data.image5} />
          </Image5Wrapper>
        </SecondGrid>

        <BlocksImagesBlocks
          blocks={Object.values(data.blocks).map((block, index) => ({
            ...block,
            // @ts-expect-error slug is a valid prop
            ...blockImages[slug][index]
          }))}
        />
      </ContainerStyle>
    </Wrapper>
  );
});

BestOfSection.displayName = 'BestOfSection';

/**
 * Export `BestOfSection` component.
 */

export default BestOfSection;
